<template>
  <div>
    <div class="screen-view-only">
      <CCard>
        <CCardBody>
          <div class="row mb-3">
            <div class="col-md-10 col-9">
              <h2 class="font-weight-bold">
                {{ $t("returnDocumentTitle") }}
              </h2>
              <p class="text-warning mb-0">
                {{ $t("returnSystemWarning") }}
              </p>
            </div>
            <div class="col-md-2 col-4 text-right align-text-center">
              <a id="transferOutDetailBackButton" @click="$router.go(-1)">
                <CButton color="dark" variant="outline">
                  <span><i class="fi fi-rr-arrow-left mr-2" style="position: relative; top: 2px;"></i></span>{{ $t('back') }}
                </CButton>
              </a>
            </div>
          </div>
          
          <div class="row mt-3 align-items-center">
            <div class="col-md-8 col-7">
              <h4 class="font-weight-bold text-primary mb-0">
                {{ $t("information") }}
              </h4>
            </div>
            <div class="col-md-4 col-5 text-right">
              <CButton 
                id="transferOutDetailDownloadButton"
                color="info" 
                variant="outline"
                class="font-weight-bold"
                @click="downloadDocument"
              >
                <span><i class="fi fi-rr-download mr-1" style="position: relative; top: 1px;"></i></span>
                {{ $t("download") }}
              </CButton>
            </div>
          </div>
                
          <CRow class="mb-3 mt-3">
            <CCol md="4" sm="12">
              <div class="h5">
                <span class="text-muted">{{ $t("returnDocumentNo") }}:</span>
                <strong id="transferOutDetailDocumentNo" class="ml-2 text-dark">{{ this.stockNo  || ''}}</strong>
              </div>
            </CCol>
          </CRow>

          <CRow>
            <CCol lg="6" col="12">
              <CRow class="form-group row">
                <label class="col-4 col-form-label h5 text-muted">
                    {{ $t("sourceBranch") }} :
                </label>
                <div class="col-8">
                  <select class="custom-select bg-light" v-model="shopObjectId" disabled>
                    <option v-for="shop in shops" :key="shop.objectId" :value="shop.objectId">
                      {{ shop.shopName }} - {{ shop.branchName }}
                    </option>
                  </select>
                </div>
              </CRow>
            </CCol>
            <CCol lg="6" col="12">
              <CRow class="form-group row">
                <label class="col-4 col-form-label h5 text-muted">
                  {{ $t("destinationShopBranch") }}  :
                </label>
                <div class="col-8">
                  <select disabled class="custom-select bg-light" v-model="destinationShop">
                    <option v-for="shop in shops" :key="shop.objectId" :value="shop.objectId">
                      {{ shop.shopName }} - {{ shop.branchName }}
                    </option>
                  </select>
                </div>
              </CRow>
            </CCol>
          </CRow>

          <CRow>
            <CCol lg="6" col="12">
              <CRow class="form-group row">
                <label class="col-4 col-form-label h5 text-muted">
                  {{ $t("createdAt") }} :
                </label>
                <div class="col-8">
                  <v-date-picker v-model="created" :locale="getDateLocale()" :disabled="true">
                    <template v-slot="{ inputValue }">
                      <input :value="formatDate(inputValue)" readonly disabled class="form-control bg-light text-dark" />
                    </template>
                  </v-date-picker>
                </div>
              </CRow>
            </CCol>
            <CCol lg="6" col="12">
              <CRow class="form-group row">
                <label class="col-4 col-form-label h5 text-muted">
                  {{ $t("referenceTransferOut") }} :
                </label>
                <div class="col-8">
                  <input class="form-control text-dark" v-model="refTransferOutNo" disabled />
                </div>
              </CRow>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CCard class="shadow-sm">
        <CCardBody>
          <div class="row mb-3">
            <div class="col-md-12 col-12">
              <h4 class="font-weight-bold text-primary">{{ $t("returnProductListTitle") }}</h4>
            </div>
          </div>

          <CRow v-if="items && items.length != 0">
            <CCol sm="12" lg="12">
              <div style="overflow-x: auto">
                <table class="table table-responsive-sm table-bordered text-dark table-striped">
                  <thead class="bg-light">
                    <tr>
                      <th style="width: 5%" class="text-center">{{ $t("number") }}</th>
                      <th style="width: 15%" class="text-left">{{ $t("pluCode") }}</th>
                      <th class="text-left">{{ $t("productName") }}</th>
                      <th style="width: 12%" class="text-center">{{ $t("unit") }}</th>
                      <th style="width: 10%" class="text-right font-weight-bold">{{ $t("returnTransferOutQty") }}</th>
                      <th style="width: 10%" class="text-right text-success font-weight-bold">{{ $t("returnDestinationReceived") }}</th>
                      <th style="width: 10%" class="text-right text-danger font-weight-bold">{{ $t("returnQtyReturned") }}</th>
                      <th style="width: 15%" class="text-left">{{ $t("transferOutNoteTitle") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- 🛠️ เพิ่มเงื่อนไขกรองสินค้า: แสดงเฉพาะรายการที่มียอดตีกลับเข้าคลังสต็อกมากกว่า 0 เท่านั้น -->
                    <tr v-for="(item, index) in items" :key="index" v-if="!item.deleted_at && (item.quantityReceived || 0) > 0">
                      <td class="text-center align-middle">{{ index + 1 }}</td>
                      <td class="align-middle text-left font-weight-bold">{{ item.productPLU.PLUCode || '-'}}</td>
                      <td class="align-middle text-left">{{ item.productPLU.name || '-'}}</td>
                      <td class="text-center align-middle">{{ item.productPLU.unit.name || '-'}}</td>
                      
                      <td class="text-right align-middle pr-3 font-weight-bold text-dark">{{ item.quantity || 0 }}</td>
                      <td class="text-right align-middle pr-3 text-success font-weight-bold">{{ item.quantity - (item.quantityReceived || 0) }}</td>
                      <td class="text-right align-middle pr-3 text-danger font-weight-bold bg-light-danger  mb-0">
                        {{ item.quantityReceived || 0 }}
                      </td>
                      <td class="text-left ">
                        <div>{{ item.note || '' }}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CCol>
          </CRow>

          <CRow class="mt-4 justify-content-end" v-if="items.length > 0">
            <CCol md="6" sm="12">
              <CCard class="border-0 shadow-sm" style="border-left: 5px solid #e55353 !important;">
                <CCardBody class="text-dark py-3">
                  <h5 class="font-weight-bold mb-3">{{ $t('returnSummaryCardTitle') }}</h5>
                  
                  <div class="d-flex justify-content-between mb-2">
                    <span class="text-muted h6 mb-0">{{ $t('returnTotalSentTO') }}:</span>
                    <span class="font-weight-bold h5 mb-0 text-dark">{{ totalTransferQty || 0 }}</span>
                  </div>
                  
                  <div class="d-flex justify-content-between mb-2">
                    <span class="text-muted h6 mb-0">{{ $t('returnTotalReceivedTI') }}:</span>
                    <span class="font-weight-bold h5 mb-0 text-success">{{ totalReceivedQty || 0 }}</span>
                  </div>
                  
                  <hr class="my-2" style="border-top: 1px solid #d8dbe0;">
                  
                  <div class="d-flex justify-content-between align-items-center mt-3">
                    <span class="font-weight-bold text-dark h5 mb-0">{{ $t('returnGrandTotalStock') }}:</span>
                    <span class="font-weight-bold h3 text-danger mb-0">{{ totalDiffQty || 0 }}</span>
                  </div>
                  <div class="mt-3 pt-2 border-top text-muted small">
                    <span class="text-danger font-weight-bold">{{ $t('returnNoteLabel') }}</span> {{ $t('returnNoteDetail') }}
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </div>

    <!-- โหมดสั่งพิมพ์ A4 / ดาวน์โหลดเอกสาร -->
    <div class="export-print-only A4-form">
      <div class="top-accent-bar"></div>
      
      <table class="header-table">
        <tr>
          <td class="company-info-block">
            <div class="company-logo">Silom POS Platform</div>
            <div class="company-details">
              141 ชั้น 11 ยูนิต 12B อาคารชุด สกุลไทย สุรวงศ์ ทาวเวอร์<br>
              ถนนสุรวงศ์ แขวงสุริยวงศ์ เขตบางรัก กรุงเทพมหานคร 10500<br>
              เลขประจำตัวผู้เสียภาษี 1234567890123
            </div>
            <div class="vendor-info-block mt-3">
              <div class="vendor-title">{{ $t("จากสาขา (ผู้ส่งคืน)") }}</div>
              <div class="vendor-name">{{ shopdetail ? shopdetail.shopName : '' }} - {{ shopdetail ? shopdetail.branchName : '' }}</div>
            </div>
          </td>
          <td class="document-meta-block">
            <div class="document-title">{{ $t("printDocumentTitle") }}</div>
            <table class="meta-data-table">
              <tr>
                <td class="meta-label">เลขที่เอกสาร</td>
                <td class="meta-value">{{ stockNo || ''}}</td>
              </tr>
              <tr>
                <td class="meta-label">วันที่</td>
                <td class="meta-value">{{ formatDate(created) }}</td>
              </tr>
              <tr>
                <td class="meta-label">อ้างอิง TO</td>
                <td class="meta-value text-primary">{{ refTransferOutNo || '-' }}</td>
              </tr>
              <tr>
                <td class="meta-label">อ้างอิง TI</td>
                <td class="meta-value text-primary">{{ refTransferInNo || '-' }}</td>
              </tr>
            </table>
          </td>
        </tr>
      </table>

      <table class="product-item-table mt-4">
        <thead>
          <tr>
            <th class="text-center" style="width: 6%;">#</th>
            <th class="text-left" style="width: 44%;">{{ $t("printProductDetailsLabel") }}</th>
            <th class="text-right" style="width: 16%;">{{ $t("returnTransferOutQty") }}</th>
            <th class="text-right" style="width: 16%;">{{ $t("returnDestinationReceived") }}</th>
            <th class="text-right text-dark font-weight-bold" style="width: 18%;">{{ $t("returnQtyReturned") }}</th>
          </tr>
        </thead>
        <tbody>
          <!-- 🛠️ เพิ่มเงื่อนไขกรองสินค้าในฝั่งพิมพ์เช่นกัน เพื่อความถูกต้องตรงกันของหน้าจอและกระดาษ -->
          <tr v-for="(item, index) in items" :key="index" v-if="!item.deleted_at && (item.quantityReceived || 0) > 0">
            <td class="text-center text-muted">{{ index + 1 }}</td>
            <td class="text-left">
              <div class="font-weight-bold text-dark">{{ item.productPLU.name || '-' }}</div>
              <div class="text-muted small">รหัส: {{ item.productPLU.PLUCode || '-' }}</div>
            </td>
            <td class="text-right text-muted">{{ item.quantity || 0 }}</td>
            <td class="text-right text-success">{{ (item.quantity - item.quantityReceived) || 0 }}</td>
            <td class="text-right font-weight-bold text-danger">
              {{ item.quantityReceived || 0 }}
            </td>
          </tr>
        </tbody>
      </table>

      <table class="summary-layout-table mt-3">
        <tr>
          <td class="summary-text-thai align-bottom">
            <!-- แสดงหมายเหตุอธิบายเกณฑ์การซ่อนสินค้าในหน้ากระดาษพิมพ์ด้านซ้าย -->
            <div class="text-muted" style="font-size: 10.5px; line-height: 1.4; padding-right: 15px;">
              <span class="text-danger font-weight-bold">{{ $t('returnNoteLabel') }}</span> {{ $t('returnNoteDetail') }}
            </div>
          </td>
          <td class="summary-digits-block">
            <table class="summary-calculation-table">
              <tr class="grand-total-row">
                <td class="calc-label text-danger">{{ $t("printReturnTotalLabel") }}</td>
                <td class="calc-value text-danger">{{ totalDiffQty }}</td>
              </tr>
            </table>
          </td>
        </tr>
      </table>

      <table class="signature-layout-table">
        <tr>
          <td class="signature-box">
            <div class="sign-meta-text">ในนาม {{ shopdetail ? shopdetail.shopName : 'ผู้รับคืน' }}</div>
            <div class="sign-space-line"></div>
            <div class="small text-muted">ผู้รับสินค้าคืนสต็อก / วันที่</div>
          </td>
          <td style="width: 10%;"></td>
          <td class="signature-box">
            <div class="sign-meta-text">ในนาม Silom POS Platform</div>
            <div class="sign-space-line"></div>
            <div class="small text-muted">ผู้อนุมัติรายการ / วันที่</div>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import util from "@/util/util";
import axios from "@/services/local";
import local from "@/services/local";
import moment from "moment";
import exportStock from '@/print/export'
import report from '@/services/report'

export default {
  components: {
    // autocomplete,
  },
  data() {
    return {
      isSuccess: false,
      alert: false,
      data: [],
      quantity: 0,
      datetime: new Date(),
      shop: [],
      inventory: [],
      branch: [],
      isVatIncluded: true,
      billNo: "",
      note: "",
      discount: 0,
      validateShop: "",
      validateItem: "",
      loadingButton: false,
      duplicateItem: "",
      destinationShop: this.$store.getters.shopObjectId,
      destinationShopDetail: "",
      validatedata: "",
      isSaveSuccess: "",
      confirmSave: false,
      footer: "",
      items: [],
      transferOut: false,
      transferIn: false,
      invalidTransferModal: false,
      message: '',
      DocType:'',
      stockNo:'',
      status:'',
      deleteModal: false,
      deleteFooter: '',
      docObjectId: '',
      docId: '',
      editSuccess: false,
      created: new Date(),
      dueDate: new Date(),
    };
  },
  computed: {
    ...mapGetters(["shops", "users", "date", "permissions"]),
    shopObjectId: {
      get() {
        return this.$store.getters.shopObjectId;
      },
      set(newValue) {
        return this.$store.dispatch("setShop", newValue);
      },
    },
    shopdetail() {
      return this.shops.find((i) => i.objectId === this.shopObjectId);
    },
    shopOptions() {
      let detail = [];
      this.shops.forEach((shop) => {
        detail.push({
          value: shop,
          label: shop.shopName + " - " + shop.branchName,
        });
      });
      return detail;
    },
    transferObjectId() {
      return this.$route.params.transferObjectId;
    },
    isDisabled() {
      return this.status === 'A' || this.status === 'C';
    },
    disableTransferButton() {
      return this.items.some(item => item.quantityReceived > item.quantity);
    },
    displayValue() {
      return item => {
        if (this.status === 'A' && item.quantityReceived < item.quantity) {
          return item.note + ', ' + this.$t('diffQuantity');
        }
        return item.note;
      };
    },
    hasInvalidQuantity() {
      return this.items.some(item => item.quantity > (item.onhandTransferIn + item.firstQuantity));
    },
    errorAlert() {
      return this.hasInvalidQuantity
    },
    
    // 🧮 คำนวณยอดรวมรายการเพื่อแสดงผลหน้าจอและส่งออกบิล
    totalTransferQty() {
      return this.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    },
    totalReceivedQty() {
      return this.items.reduce((sum, item) => sum + Number(item.quantity - item.quantityReceived || 0), 0);
    },
    totalDiffQty() {
      return this.totalTransferQty - this.totalReceivedQty;
    },
    refTransferOutNo() {
      return this.data?.refDocument?.stockNo || '';
    },
    refTransferInNo() {
      return this.data?.refDocument?.stockNo || '';
    },

    // 📦 รวบรวมและจัดโครงสร้าง Data สำหรับส่งไป Map ในไฟล์ EJS Template
   // 📦 รวบรวมและจัดโครงสร้าง Data สำหรับส่งไป Map ในไฟล์ EJS Template
  // 📦 รวบรวมและจัดโครงสร้างข้อมูลจับคู่ส่งเข้าโมดูลพิมพ์ใบรับสินค้าคืน (Product Return Form)
    setExportdata() {
      // 1. ค้นหารายละเอียดคลังต้นทาง (คลังรับคืนสินค้าเข้าสต็อก)
      const sourceBranchDetail = this.shops.find((i) => i.objectId === this.shopObjectId);
      
      return {
        stockNo: this.stockNo || '-',
        created_at: this.created ? moment(this.created).format('DD/MM/YYYY') : '-',
        refTransferOutNo: this.refTransferOutNo || '-',
        refTransferInNo: this.refTransferInNo || '-',
        note: this.note || '',
        
        // ข้อมูลสาขาคลังหลักต้นทาง (ผู้รับคืน)
        shop: sourceBranchDetail,
        
        // ข้อมูลสาขาปลายทาง (ผู้ส่งคืน)
        destinationShop: {
          shopName: this.destinationShopDetail ? this.destinationShopDetail.shopName : '',
          branchName: this.destinationShopDetail ? this.destinationShopDetail.branchName : '',
        },
        
        // รายการสินค้า (ลูปกรองสินค้าที่ถูกลบออก และแปลงประเภทตัวเลขให้แม่นยำ)
        items: (this.items || []).filter(item => !item.deleted_at).map((item, index) => {
          const qtyOut = Number(item.quantity || 0);
          const qtyRec = Number(item.quantityReceived || 0);
          
          return {
            index: index + 1,
            productPLU: {
              PLUCode: item.productPLU?.PLUCode || '-',
              name: item.productPLU?.name || '-',
              unit: {
                name: item.productPLU?.unit?.name || 'ชิ้น'
              }
            },
            quantity: qtyOut,                  // จำนวนที่เคยตั้งโอนออก
            quantityReceived: qtyOut - qtyRec, // คำนวณปลายทางรับจริงตามตรรกะหน้าจอ
            quantityReturned: qtyRec    ,       // จำนวนที่โดนตีกลับจริงเข้าสต็อกคลังเดิม
            note: item.note || ''             // หมายเหตุการตีกลับ (ถ้ามี)
          };
        }),
        
        // สรุปผลยอดรวมท้ายเอกสาร
        totalTransferQty: Number(this.totalTransferQty || 0),
        totalReceivedQty: Number(this.totalReceivedQty || 0),
        totalDiffQty: Number(this.totalDiffQty || 0)
      };
    }
  },
  created() {
    this.getTransferDetail()
  },
  mounted() {
    this.$store.dispatch("getShop");
    this.$store.dispatch("getUser");
  },
  watch: {
    '$i18n.locale': 'updateDocType',
  },
  methods: {
    ...util,
    
    // ⚡️ ผูกปุ่มดาวน์โหลดเอกสารจาก Template เดิมให้เรียกใช้งานฟังก์ชัน Export Report PDF ข้างล่าง
    downloadDocument() {
      this.exportReport();
    },
    exportReport() {
      const setdata = this.setExportdata
      const uid = `${localStorage.getItem('shopsUid')}`
      
      // ส่งข้อมูลเข้า ejs template เพื่อทำโครง HTML
      let html = exportStock.exportProductReturn(setdata)
      
      let data = {
        ref: this.shopObjectId,
        timezone: 'Asia/Bangkok',
        html: html,
      }
      let name = 'เอกสารตีกลับสินค้า_' + this.stockNo + '.pdf'
      const headers = {
        shopObjectId: util.sanitizeShopObjectId(this.shopObjectId),
      }

      report({
        method: 'post',
        url: '/print/v1.0/' + uid + '/htmltopdf/a4',
        data: data,
        headers: headers,
        responseType: 'blob',
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }).catch((error) => {
        console.error("เกิดข้อผิดพลาดในการส่งออก PDF: ", error);
      });
    },
    formatDate(date) {
      if (!date) return ''
      if (typeof date === 'string') {
        if (this.$i18n.locale === 'en') {
          return moment(date, 'MM/DD/YYYY').format('DD/MM/YYYY')
        }
        if (this.$i18n.locale === 'th') {
          return moment(date, 'DD/MM/YYYY').format('DD/MM/YYYY')
        }
        return moment(date).format('DD/MM/YYYY')
      }
      return moment(date).format('DD/MM/YYYY')
    },
    calculateOnhandTransferOut(item) {
      if (item.quantity === item.firstQuantity) {
        return item.onhandTransferIn;
      } else {
        return Number((item.onhandTransferIn + item.firstQuantity) - item.quantity);
      }
    },
    setEditSuccess() {
      this.editSuccess = true
      window.scrollTo({ top: 0 })
      setTimeout(() => {
        this.editSuccess = false
      }, 3000)
    },
    isDiffQuantity(item) {
      return this.status === 'A' && item.quantityReceived < item.quantity;
    },
    updateNote(event, item) {
      const inputValue = event.target.value;
      const diffText = ', ' + this.$t('diffQuantity');
      if (this.status === 'A' && item.quantityReceived < item.quantity) {
        if (inputValue.endsWith(diffText)) {
          item.note = inputValue.slice(0, -diffText.length);
        } else {
          item.note = inputValue;
        }
      } else {
        item.note = inputValue;
      }
    },
    updateDocType() {
      if(this.transferIn) {
        this.DocType = this.$t('transferInTxt');
      } else if(this.transferOut) {
        this.DocType = this.$t('transferOutTxt');
      }
    },
    getDateLocale() {
        return this.$i18n.locale
    },
    customLabel(option) {
      return `${option.PLUCode} - ${option.name}`;
    },
    removeProduct(index) {
      let items = this.items.map(item => {
        if (item.index === index) {
          return { ...item, deleted_at: new Date().toISOString() };
        }
        return item;
      });
      this.items = items;
    },
    editSuccessModal(){
      this.editSuccess = false
      this.getTransferDetail()
    },
    validateDoc() {
      if (this.shopObjectId == this.destinationShop) {
        this.validatedata = this.$i18n.t("selectdestinationPlease");
      } else if (this.destinationShop == "") {
        this.validatedata = this.$i18n.t("selectdestinationPlease");
      } else {
        if (this.items.length == 0) {
          this.validatedata = this.$i18n.t("validateProduct");
        } else {
          this.validatedata = "";
          this.confirmSave = true;
        }
      }
    },
    getTransferDetail(){
        this.loadingButton = false;
        const uid = `${localStorage.getItem("shopsUid")}`;
        const shopObjectId = this.shopObjectId;
        this.loading = true;
        const transferObjectId = this.$route.params.objectId;
        const params = { shopObjectId : shopObjectId }
        const headers = {
          shopObjectId: util.sanitizeShopObjectId(this.$store.getters.shopObjectId),
        }
        axios({
        method: 'get',
        url: '/api/v1.0/' + uid + '/stockdocument/transfer/' + transferObjectId,
        params: params,
        headers: headers
      }).then((res) => {
        if (res.data.error.code === 4000){
            this.invalidTransferModal = true
            if(res.data.error.message = 'Stock document not found'){
              this.message = this.$t('TransferDocNotFound')
            }
          }else{
            this.data = res.data.data;  
            this.loadingButton = false;
            this.loading = false;
    
            this.destinationShop = this.data.source?.objectId || ''
            this.destinationShopDetail = this.data.source
            this.items = this.data.items
            this.DocType = this.$t('transferInTxt')
            this.stockNo = this.data.stockNo || ''
            this.status = this.data.status
            this.note = this.data.note
            this.docId = this.data.id
            this.docObjectId = this.data.objectId
            this.created = this.data.created_at
            this.dueDate = this.data.dueDate
            
            this.searchProductPLU()
          }
          })
          .catch((error) => {
            console.log(error);
          });
    },
    searchProductPLU() {
      if (this.shopObjectId === "") {
        this.validateShop = this.$i18n.t("selectShopPlease");
      } else {
        this.validateShop = "";
      }
      const uid = `${localStorage.getItem("shopsUid")}`;
      let params = { shopObjectId: this.$store.getters.shopObjectId, page: 1, limit: 50 };
      if (this.keywordEntered && this.keywordEntered !== "") {
        params.keyword = this.keywordEntered;
      }
      const headers = { shopObjectId: util.sanitizeShopObjectId(this.$store.getters.shopObjectId) };
      axios({
        url: "/api/v1.0/" + uid + "/getproductpluwithstock/paginate",
        params: params,
        headers: headers,
        method: "GET",
      })
        .then((response) => {
          this.alert = false;
          const productPLUWithStock = response.data.data
          productPLUWithStock.forEach((item) => {
            const matchedItem = this.items.find(
              (transferItem) => transferItem.productPLU.id === item.id
            )
            if (matchedItem) {
              this.$set(matchedItem, 'onhandTransferIn', item.ProductPLUStock.onhandQty)
            }
          })
        })
        .catch((e) => {
          this.alert = true;
          console.log(e);
        });
    },
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style src="../../../assets/styles/inventory.css" scoped></style>
<style>
.multiselect__tags {
  min-height: 35px !important;
  height: 35px !important;
  padding-top: 6px !important;
}
</style>
<style scoped>
/* ==========================================================================
   1. โหมดปกติแสดงผลบนหน้าจอคอมพิวเตอร์ (Screen View)
   ========================================================================== */
.screen-view-only {
  display: block; /* หน้าจอปกติให้เปิดแสดงผลขึ้นมาตามมาตรฐานของระบบ */
}

.export-print-only {
  display: none; /* ซ่อนเทมเพลตสไตล์ FlowAccount เอาไว้ ไม่ให้แสดงบนหน้าจอปกติ */
}

/* ==========================================================================
   2. โหมดสั่งปริ้นท์ หรือดาวน์โหลด PDF (Print Media / Export)
   ========================================================================== */
@media print {
  @page {
    size: A4;
    margin: 15mm 12mm 15mm 12mm;
  }

  /* สลับโหมด: ซ่อนหน้าตาของระบบ แล้วเปิดโครงสร้างของ FlowAccount ขึ้นมาแทน */
  .screen-view-only {
    display: none !important;
  }

  .export-print-only {
    display: block !important;
    background-color: #ffffff;
    box-shadow: none !important;
    width: 100% !important;
    font-family: 'Sukhumvit Set', sans-serif;
  }

  /* -------------------------------------------
     สไตล์ดีไซน์รูปแบบ FlowAccount สำหรับกระดาษ A4
     ------------------------------------------- */
  .top-accent-bar {
    position: absolute;
    top: 0; right: 0; width: 0; height: 0;
    border-style: solid; border-width: 0 60px 60px 0;
    border-color: transparent #a75d5d transparent transparent;
  }
  .header-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  .company-info-block { width: 55%; vertical-align: top; }
  .company-logo { font-size: 20px; font-weight: bold; color: #314455; }
  .company-details { font-size: 11px; color: #666; line-height: 1.5; }
  .vendor-title { font-size: 11px; font-weight: bold; color: #a75d5d; }
  .vendor-name { font-size: 13px; font-weight: bold; }
  .document-meta-block { width: 45%; vertical-align: top; text-align: right; }
  .document-title { font-size: 24px; font-weight: bold; color: #a75d5d; margin-bottom: 5px; }
  .meta-data-table { width: 85%; margin-left: auto; border-collapse: collapse; }
  .meta-data-table td { padding: 4px 0; font-size: 12px; border-bottom: 1px dashed #eaeaea; }
  .meta-label { text-align: left; color: #777; }
  .meta-value { text-align: right; font-weight: bold; }
  .product-item-table { width: 100%; border-collapse: collapse; }
  .product-item-table th { border-top: 1px solid #333; border-bottom: 1px solid #333; padding: 8px 4px; font-size: 12px; }
  .product-item-table td { border-bottom: 1px solid #efefef; padding: 10px 4px; font-size: 12px; }
  .summary-layout-table { width: 100%; border-collapse: collapse; }
  .summary-text-thai { width: 50%; font-size: 12px; color: #777; font-style: italic; }
  .summary-digits-block { width: 50%; }
  .summary-calculation-table { width: 100%; border-collapse: collapse; }
  .summary-calculation-table td { padding: 5px 6px; font-size: 12px; }
  .calc-label { text-align: right; color: #666; }
  .calc-value { text-align: right; font-weight: bold; }
  .grand-total-row td { border-top: 1px solid #333; border-bottom: 4px double #333; font-size: 14px; font-weight: bold; }
  .signature-layout-table { width: 100%; border-collapse: collapse; margin-top: 40px; }
  .signature-box { width: 45%; border: 1px solid #e0e0e0; border-radius: 4px; padding: 12px; text-align: center; }
  .sign-meta-text { font-size: 11px; margin-bottom: 35px; }
  .sign-space-line { width: 80%; margin: 0 auto 5px auto; border-bottom: 1px solid #b5b5b5; }
}
</style>
